import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip, { TooltipSection } from "../cards/subcomponents/CustomTooltip";
import { SurfaceCard } from "./SurfaceCard";
import { Sparkline } from "./Sparkline";
import { tokens } from "../../theme";

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ElementType;
  /** Trend drawn edge to edge at the bottom of the tile. */
  trend?: number[];
  trendColor?: string;
  /** Top-right slot next to the icon. */
  aside?: React.ReactNode;
  /** Custom content for the bottom slot when there is no trend. */
  footer?: React.ReactNode;
  tooltipSections?: TooltipSection[];
}

/** KPI tile: gold icon chip, quiet label, figure, context line and a bleeding sparkline. */
export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  sub,
  icon: Icon,
  trend,
  trendColor = tokens.gold,
  aside,
  footer,
  tooltipSections,
}) => {
  const hasTrend = !!trend && trend.length > 1;
  const hasBottom = hasTrend || !!footer;

  const tile = (
    <SurfaceCard
      radius="lg"
      padding="none"
      sx={{ height: "100%", display: "flex", flexDirection: "column", cursor: tooltipSections ? "help" : undefined }}
    >
      <Box sx={{ px: { xs: 2, md: 2.25 }, pt: { xs: 2, md: 2.25 }, pb: hasBottom ? 0 : { xs: 2, md: 2.25 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box
            sx={{
              width: { xs: 30, md: 34 },
              height: { xs: 30, md: 34 },
              borderRadius: { xs: "10px", md: "12px" },
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(205,160,117,0.14)",
              border: "1px solid rgba(205,160,117,0.30)",
              color: tokens.goldLight,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: { xs: 16, md: 18 } }} />
          </Box>
          {aside}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: { xs: 1.5, md: 1.75 }, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: { xs: "0.75rem", md: "0.8125rem" }, fontWeight: 500, color: "text.secondary" }}>
            {label}
          </Typography>
          {tooltipSections && <InfoOutlinedIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />}
        </Stack>
        <Typography
          component="div"
          noWrap
          sx={{
            fontSize: { xs: "1.3125rem", md: "1.5rem" },
            fontWeight: 650,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            mt: 0.5,
          }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography component="div" noWrap sx={{ fontSize: "0.75rem", color: "text.disabled", mt: 0.25 }}>
            {sub}
          </Typography>
        )}
      </Box>
      {hasTrend ? (
        <Box sx={{ mt: "auto", pt: 1.5 }}>
          <Sparkline data={trend!} color={trendColor} height={44} fill />
        </Box>
      ) : footer ? (
        <Box sx={{ mt: "auto", px: { xs: 2, md: 2.25 }, pt: 1.5, pb: { xs: 2, md: 2.25 } }}>{footer}</Box>
      ) : null}
    </SurfaceCard>
  );

  if (!tooltipSections) return tile;
  return (
    <CustomTooltip placement="bottom" sections={tooltipSections} fullWidth>
      {tile}
    </CustomTooltip>
  );
};

export default StatTile;
